import React from "react";
import { Link, Navigate } from "react-router-dom";
import { ArrowRight, Twitter, Instagram, Linkedin } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

const serif = "font-['Nunito']";
const sans = "font-['Nunito']";

/* ═══════════════════════════════════════════════════════════
   HeroAnimation — Animated Gantt chart in pure SVG.
   Bars grow, dependencies draw, today line sweeps in,
   and progress fills reveal completion status.
   ═══════════════════════════════════════════════════════════ */
function HeroAnimation() {
  const barH = 20,
    barHs = 14;
  const acts = [
    {
      id: 1,
      label: "Site Prep",
      x: 155,
      w: 74,
      row: 0,
      crit: true,
      prog: 1.0,
      dur: "5d",
    },
    {
      id: 2,
      label: "Excavation",
      x: 232,
      w: 86,
      row: 1,
      crit: true,
      prog: 1.0,
      dur: "12d",
    },
    {
      id: 3,
      label: "Foundation",
      x: 320,
      w: 128,   
      row: 2,
      crit: true,
      prog: 0.65,
      dur: "18d",
    },
    {
      id: 4,
      label: "Superstructure",
      x: 452,
      w: 148,
      row: 3,
      crit: true,
      prog: 0,
      dur: "24d",
    },
    {
      id: 5,
      label: "MEP Systems",
      x: 244,
      w: 150,
      row: 4,
      crit: false,
      prog: 0.78,
      dur: "15d",
    },
    {
      id: 6,
      label: "Finishes",
      x: 406,
      w: 108,
      row: 5,
      crit: false,
      prog: 0.05,
      dur: "10d",
    },
  ];

  const rowTop = (i) => 40 + i * 48;
  const barY = (a) =>
    rowTop(a.row) + (a.crit ? (48 - barH) / 2 : (48 - barHs) / 2);
  const barMid = (a) => barY(a) + (a.crit ? barH / 2 : barHs / 2);

  const deps = [
    { from: 1, to: 2, crit: true },
    { from: 2, to: 3, crit: true },
    { from: 3, to: 4, crit: true },
    { from: 1, to: 5, crit: false },
    { from: 5, to: 6, crit: false },
  ];
  const actMap = Object.fromEntries(acts.map((a) => [a.id, a]));
  const weekX = [155, 238, 321, 404, 487, 570, 650];
  const todayX = 400;

  return (
    <div className="relative w-full" style={{ aspectRatio: "680 / 340" }}>
      <style>{`
        @keyframes gFadeIn { from { opacity: 0 } to { opacity: 1 } }
        @keyframes gSlideUp { from { opacity: 0; transform: translateY(6px) } to { opacity: 1; transform: translateY(0) } }
        @keyframes gBarGrow { from { transform: scaleX(0) } to { transform: scaleX(1) } }
        @keyframes gDraw { to { stroke-dashoffset: 0 } }
        @keyframes gSweep { from { transform: scaleY(0) } to { transform: scaleY(1) } }
        @keyframes gPulse { 0%,100% { opacity: 0.3; transform: scale(1) } 50% { opacity: 0.6; transform: scale(1.3) } }
        @keyframes gGlow { 0%,100% { opacity: 0.2 } 50% { opacity: 0.45 } }
        @keyframes floatCardIn { 0% { opacity: 0; transform: translateY(10px) scale(0.96) } 100% { opacity: 1; transform: translateY(0) scale(1) } }
        .g-fade { opacity: 0; animation: gFadeIn 0.5s ease forwards }
        .g-label { opacity: 0; animation: gSlideUp 0.45s ease forwards }
        .g-bar { transform: scaleX(0); animation: gBarGrow 0.7s cubic-bezier(0.22, 1, 0.36, 1) forwards }
        .g-prog { transform: scaleX(0); animation: gBarGrow 0.55s cubic-bezier(0.22, 1, 0.36, 1) forwards }
        .g-dep { animation: gDraw 0.65s cubic-bezier(0.4, 0, 0.2, 1) forwards }
        .g-today { transform-origin: top center; transform: scaleY(0); animation: gSweep 0.45s cubic-bezier(0.22, 1, 0.36, 1) forwards }
        .g-dot { animation: gPulse 2.2s ease-in-out infinite }
        .g-glow { animation: gGlow 2.5s ease-in-out infinite }
      `}</style>

      <svg
        viewBox="0 0 680 340"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="h-full w-full"
      >
        <defs>
          <linearGradient id="gBar" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#A08460" />
            <stop offset="100%" stopColor="#C4A97D" />
          </linearGradient>
          <linearGradient id="gBarDone" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#7B6645" />
            <stop offset="100%" stopColor="#9C7F58" />
          </linearGradient>
          <linearGradient id="gBarSlate" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#CBD5E1" />
            <stop offset="100%" stopColor="#E2E8F0" />
          </linearGradient>
          <filter id="gGlowF" x="-30%" y="-30%" width="160%" height="160%">
            <feGaussianBlur stdDeviation="5" />
          </filter>
        </defs>

        {/* Alternating row bands */}
        {[0, 2, 4].map((i) => (
          <rect
            key={`band-${i}`}
            x="0"
            y={rowTop(i)}
            width="680"
            height="48"
            fill="#F8FAFC"
            className="g-fade"
            style={{ animationDelay: "0.05s" }}
          />
        ))}

        {/* Vertical week gridlines */}
        <g className="g-fade" style={{ animationDelay: "0.1s" }}>
          {weekX.map((x, i) => (
            <line
              key={`grid-${i}`}
              x1={x}
              y1="26"
              x2={x}
              y2="328"
              stroke="#E2E8F0"
              strokeWidth="0.5"
            />
          ))}
        </g>

        {/* Week labels */}
        {["W1", "W2", "W3", "W4", "W5", "W6"].map((w, i) => (
          <text
            key={w}
            x={(weekX[i] + weekX[i + 1]) / 2}
            y="16"
            textAnchor="middle"
            className="g-label"
            style={{
              animationDelay: `${0.2 + i * 0.04}s`,
              fontSize: "9.5px",
              fontFamily: "'DM Sans', sans-serif",
              fontWeight: 500,
              fill: "#94A3B8",
              letterSpacing: "0.06em",
            }}
          >
            {w}
          </text>
        ))}

        {/* Activity row labels */}
        {acts.map((a, i) => (
          <text
            key={`lbl-${a.id}`}
            x="144"
            y={barMid(a)}
            textAnchor="end"
            dominantBaseline="central"
            className="g-label"
            style={{
              animationDelay: `${0.35 + i * 0.08}s`,
              fontSize: a.crit ? "10px" : "9px",
              fontFamily: "'DM Sans', sans-serif",
              fontWeight: a.crit ? 600 : 400,
              fill: a.crit ? "#1E293B" : "#64748B",
            }}
          >
            {a.label}
          </text>
        ))}

        {/* Activity bars */}
        {acts.map((a, i) => {
          const h = a.crit ? barH : barHs;
          const y = barY(a);
          return (
            <g key={`bar-${a.id}`}>
              {/* Glow under critical bars */}
              {a.crit && (
                <rect
                  x={a.x}
                  y={y - 3}
                  width={a.w}
                  height={h + 6}
                  rx="6"
                  fill="#A08460"
                  opacity="0"
                  filter="url(#gGlowF)"
                  className="g-bar g-glow"
                  style={{
                    transformOrigin: `${a.x}px center`,
                    animationDelay: `${0.55 + i * 0.13}s, ${2.6}s`,
                  }}
                />
              )}
              {/* Main bar */}
              <rect
                x={a.x}
                y={y}
                width={a.w}
                height={h}
                rx={a.crit ? 5 : 3.5}
                fill={a.crit ? "url(#gBar)" : "url(#gBarSlate)"}
                className="g-bar"
                style={{
                  transformOrigin: `${a.x}px center`,
                  animationDelay: `${0.55 + i * 0.13}s`,
                }}
              />
              {/* Progress fill */}
              {a.prog > 0 && (
                <g>
                  <clipPath id={`cp-${a.id}`}>
                    <rect
                      x={a.x}
                      y={y}
                      width={a.w}
                      height={h}
                      rx={a.crit ? 5 : 3.5}
                    />
                  </clipPath>
                  <rect
                    x={a.x}
                    y={y}
                    width={a.w * a.prog}
                    height={h}
                    fill={a.crit ? "url(#gBarDone)" : "#94A3B8"}
                    clipPath={`url(#cp-${a.id})`}
                    className="g-prog"
                    style={{
                      transformOrigin: `${a.x}px center`,
                      animationDelay: `${1.6 + i * 0.1}s`,
                    }}
                  />
                </g>
              )}
              {/* Duration label */}
              <text
                x={a.x + a.w + 7}
                y={barMid(a)}
                dominantBaseline="central"
                className="g-label"
                style={{
                  animationDelay: `${1.4 + i * 0.09}s`,
                  fontSize: "8px",
                  fontFamily: "'DM Sans', sans-serif",
                  fontWeight: 600,
                  fill: a.crit ? "#A08460" : "#94A3B8",
                }}
              >
                {a.dur}
              </text>
            </g>
          );
        })}

        {/* Dependency curves */}
        {deps.map((d, i) => {
          const from = actMap[d.from];
          const to = actMap[d.to];
          const x1 = from.x + from.w;
          const y1 = barMid(from);
          const x2 = to.x;
          const y2 = barMid(to);
          const mx = (x1 + x2) / 2;
          return (
            <path
              key={`dep-${i}`}
              d={`M${x1},${y1} C${mx},${y1} ${mx},${y2} ${x2},${y2}`}
              stroke={d.crit ? "#C4A97D" : "#CBD5E1"}
              strokeWidth={d.crit ? 1.5 : 1}
              fill="none"
              strokeDasharray="200"
              strokeDashoffset="200"
              className="g-dep"
              style={{ animationDelay: `${1.9 + i * 0.1}s` }}
            />
          );
        })}

        {/* Milestone diamond */}
        <g className="g-label" style={{ animationDelay: "2.4s" }}>
          <polygon
            points={`${actMap[4].x + actMap[4].w + 10},${barMid(actMap[4])} ${actMap[4].x + actMap[4].w + 17},${barMid(actMap[4]) - 7} ${actMap[4].x + actMap[4].w + 24},${barMid(actMap[4])} ${actMap[4].x + actMap[4].w + 17},${barMid(actMap[4]) + 7}`}
            fill="#8B7355"
            stroke="#6B5A42"
            strokeWidth="0.8"
          />
        </g>

        {/* Today line */}
        <g className="g-today" style={{ animationDelay: "2.5s" }}>
          <line
            x1={todayX}
            y1="24"
            x2={todayX}
            y2="326"
            stroke="#F43F5E"
            strokeWidth="1.5"
            strokeDasharray="5 3"
            opacity="0.7"
          />
          <circle
            cx={todayX}
            cy="24"
            r="3"
            fill="#F43F5E"
            className="g-dot"
            style={{ animationDelay: "3s" }}
          />
          <rect
            x={todayX - 18}
            y="4"
            width="36"
            height="15"
            rx="7.5"
            fill="#F43F5E"
          />
          <text
            x={todayX}
            y="14.5"
            textAnchor="middle"
            style={{
              fontSize: "7.5px",
              fontFamily: "'DM Sans', sans-serif",
              fontWeight: 700,
              fill: "white",
              letterSpacing: "0.06em",
            }}
          >
            TODAY
          </text>
        </g>

        {/* Completion checks on finished bars */}
        {acts
          .filter((a) => a.prog >= 1)
          .map((a) => (
            <g
              key={`chk-${a.id}`}
              className="g-label"
              style={{ animationDelay: "2.2s" }}
            >
              <circle
                cx={a.x + a.w - 2}
                cy={barY(a) - 5}
                r="6"
                fill="#059669"
              />
              <path
                d={`M${a.x + a.w - 5},${barY(a) - 5} l2.5,2.5 l4,-5`}
                stroke="white"
                strokeWidth="1.3"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
              />
            </g>
          ))}
      </svg>
    </div>
  );
}

function LandingPage() {
  const { isAuthenticated, user, loading } = useAuth();

  // If already logged in, redirect to dashboard
  if (!loading && isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="relative min-h-screen bg-[#FAFAF7] selection:bg-slate-900 selection:text-white">
      {/* ───────────────────── NAV ───────────────────── */}
      <nav className="relative z-40 mx-auto flex max-w-[1200px] items-center justify-between px-6 py-5 md:px-10 lg:py-7">
        <Link
          to="/"
          className={`${serif} text-[1.35rem] leading-none text-slate-900`}
        >
          <span>epicplan</span>
          <span className={`${sans} my-1 block text-[10px] font-medium tracking-[0.08em] text-slate-400 not-italic`}>Create Epic Plans</span>
        </Link>
        <div className="flex items-center gap-2">
          <Link
            to="/login"
            className={`${sans} rounded-full px-4 py-2 text-[13px] font-medium tracking-[-0.01em] text-slate-600 transition hover:text-slate-900`}
          >
            Sign in
          </Link>
          <Link
            to="/create-organization"
            className={`${sans} rounded-full bg-slate-900 px-5 py-2 text-[13px] font-medium tracking-[-0.01em] text-white transition hover:bg-slate-800`}
          >
            Start free
          </Link>
        </div>
      </nav>

      {/* ───────────────────── HERO ───────────────────── */}
      <section className="relative mx-auto max-w-[1200px] px-6 pb-20 pt-6 md:px-10 md:pb-28 md:pt-12 lg:pt-16">
        <div className="grid items-center gap-8 lg:grid-cols-[1fr_1.05fr] lg:gap-14">
          {/* ── Left: Copy ── */}
          <div>
            <p
              className={`${sans} animate-in fade-in slide-in-from-bottom-2 duration-500 text-[11.5px] font-semibold uppercase tracking-[0.2em] text-slate-400`}
            >
              construction Scheduling, upgraded.
            </p>

            <h1
              className={`${serif} animate-in fade-in slide-in-from-bottom-4 delay-75 duration-700 mt-5 text-[clamp(2.6rem,5.8vw,4.8rem)] leading-[0.98] tracking-[-0.035em] text-slate-900`}
            >
              Every epic build
              <br className="hidden sm:block" /> starts with an{" "}
              <span className=" text-[#8B7355]">epicplan.</span>
            </h1>

            <p
              className={`${sans} animate-in fade-in slide-in-from-bottom-4 delay-150 duration-700 mt-6 max-w-[480px] text-[1rem] font-light leading-[1.7] tracking-[-0.01em] text-slate-500`}
            >
              AI-assisted critical-path scheduling, baseline tracking, earned value metrics,
              and schedule analytics — in one calm, purposeful workspace.
            </p>

            <div className="animate-in fade-in slide-in-from-bottom-4 delay-200 duration-700 mt-9 flex items-center gap-4">
              <Link
                to="/create-organization"
                className={`${sans} group inline-flex items-center gap-2.5 rounded-full bg-slate-900 py-3 pl-6 pr-5 text-[14px] font-medium text-white transition hover:bg-slate-800`}
              >
                Get started
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </Link>
              <Link
                to="/login"
                className={`${sans} inline-flex items-center py-3 text-[14px] font-medium text-slate-400 underline-offset-4 transition hover:text-slate-900 hover:underline`}
              >
                I have an account
              </Link>
            </div>
          </div>

          {/* ── Right: Animated Gantt Chart ── */}
          <div className="relative animate-in fade-in zoom-in-[0.97] delay-200 duration-700">
            {/* Subtle warm glow behind card */}
            <div className="absolute -inset-4 -z-10 rounded-3xl bg-[#8B7355]/[0.04] blur-2xl" />

            {/* Main card frame */}
            <div className="rounded-2xl border border-slate-200/50 bg-white/70 p-3 shadow-[0_8px_40px_rgb(0,0,0,0.04)] backdrop-blur-sm">
              <HeroAnimation />
            </div>

            {/* Floating: Schedule Health — top right */}
            <div
              className="absolute -right-5 -top-5 z-10 hidden rounded-xl border border-white/80 bg-white/90 px-4 py-3 shadow-[0_4px_24px_rgb(0,0,0,0.08)] backdrop-blur-lg lg:block"
              style={{
                animation: "floatCardIn 0.6s ease both",
                animationDelay: "1.8s",
              }}
            >
              <p
                className={`${sans} text-[9.5px] font-semibold uppercase tracking-[0.12em] text-slate-400`}
              >
                Schedule Health
              </p>
              <div className="mt-1 flex items-baseline gap-1">
                <span
                  className={`${serif} text-[1.5rem] leading-none text-emerald-600`}
                >
                  94
                </span>
                <span
                  className={`${sans} text-[11px] font-medium text-emerald-500`}
                >
                  %
                </span>
              </div>
            </div>

            {/* Floating: Activities — bottom left */}
            <div
              className="absolute -bottom-4 -left-3 z-10 hidden items-center gap-2.5 rounded-full border border-white/80 bg-white/90 px-4 py-2 shadow-[0_4px_24px_rgb(0,0,0,0.08)] backdrop-blur-lg lg:flex"
              style={{
                animation: "floatCardIn 0.6s ease both",
                animationDelay: "2.2s",
              }}
            >
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
              </span>
              <span
                className={`${sans} text-[11.5px] font-medium text-slate-700`}
              >
                12 Activities on track
              </span>
            </div>

            {/* Floating: Duration — right middle */}
            <div
              className="absolute -right-6 top-[55%] z-10 hidden -translate-y-1/2 rounded-lg bg-slate-900 px-3.5 py-2.5 shadow-xl lg:block"
              style={{
                animation: "floatCardIn 0.6s ease both",
                animationDelay: "2.5s",
              }}
            >
              <p
                className={`${sans} text-[9px] font-medium uppercase tracking-[0.1em] text-slate-500`}
              >
                Total Duration
              </p>
              <p
                className={`${sans} mt-0.5 text-[15px] font-semibold tracking-tight text-white`}
              >
                65 days
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ───────────────────── DIVIDER ───────────────────── */}
      <div className="mx-auto max-w-[1200px] px-6 md:px-10">
        <div className="h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent" />
      </div>

      {/* ───────────────────── CAPABILITIES ───────────────────── */}
      <section className="mx-auto max-w-[1200px] px-6 py-20 md:px-10 md:py-28">
        <p
          className={`${sans} text-[11.5px] font-semibold uppercase tracking-[0.2em] text-slate-400`}
        >
          What you get
        </p>
        <h2
          className={`${serif} mt-4 text-[clamp(2rem,3.8vw,3.2rem)] leading-[1.05] tracking-[-0.03em] text-slate-900`}
        >
          Historical Data. Deeper clarity.
        </h2>

        <div className="mt-16 space-y-0">
          {[
            {
              num: "01",
              title: "AI Schedule Assistant",
              body: "Use your company's historical schedule data to instantly generate schedule activities based on market sector — then refine them!",
            },
            {
              num: "02",
              title: "Critical Path Engine",
              body: "Forward pass, backward pass, float analysis. Dependencies resolve in real-time so your schedule stays honest — not optimistic.",
            },
            {
              num: "03",
              title: "Baseline & Scenario Control",
              body: "Snapshot your plan, then explore what-if variations without risk. Compare scenarios side-by-side to make decisions you can defend.",
            },
            {
              num: "04",
              title: "Schedule Analytics",
              body: "Analyze your schedule and progress six ways to Sunday and surface insights you did not previously have access to.",
            },
          ].map((feat) => (
            <div
              key={feat.num}
              className="grid items-baseline gap-4 border-t border-slate-200/70 py-7 md:grid-cols-[50px_1fr_1.2fr] md:gap-8 md:py-9"
            >
              <span
                className={`${sans} text-[12px] font-semibold text-slate-300`}
              >
                {feat.num}
              </span>
              <h3
                className={`${serif} text-[1.5rem] leading-[1.15] tracking-[-0.02em] text-slate-900 md:text-[1.7rem]`}
              >
                {feat.title}
              </h3>
              <p
                className={`${sans} text-[0.92rem] font-light leading-[1.72] text-slate-500`}
              >
                {feat.body}
              </p>
            </div>
          ))}
        </div>
      </section>



      {/* ───────────────────── WORKFLOW ───────────────────── */}
      <section className="mx-auto max-w-[1200px] px-6 py-20 md:px-10 md:py-28">
        <div className="grid gap-14 lg:grid-cols-2 lg:items-center">
          <div>
            <p
              className={`${sans} text-[11.5px] font-semibold uppercase tracking-[0.2em] text-slate-400`}
            >
              How it flows
            </p>
            <h2
              className={`${serif} mt-4 text-[clamp(2rem,3.8vw,3rem)] leading-[1.08] tracking-[-0.03em] text-slate-900`}
            >
              From blank canvas
              <br />
              to baseline in minutes.
            </h2>
          </div>
          <div>
            {[
              {
                step: "Create",
                desc: "Start a project, define calendars and WBS hierarchy.",
              },
              {
                step: "Schedule",
                desc: "Add activities, link dependencies, or let AI generate the first draft.",
              },
              {
                step: "Analyze",
                desc: "Run CPM, review the critical path, check float and earned value.",
              },
              {
                step: "Baseline",
                desc: "Lock your plan, compare progress, and iterate with scenarios.",
              },
            ].map((s, i) => (
              <div
                key={s.step}
                className="flex gap-5 border-t border-slate-200/70 py-5"
              >
                <span
                  className={`${serif} mt-0.5 text-[1.7rem] leading-none text-slate-200`}
                >
                  {i + 1}
                </span>
                <div>
                  <p
                    className={`${sans} text-[14px] font-medium tracking-[-0.01em] text-slate-900`}
                  >
                    {s.step}
                  </p>
                  <p
                    className={`${sans} mt-1 text-[13.5px] font-light leading-relaxed text-slate-500`}
                  >
                    {s.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ───────────────────── CTA ───────────────────── */}
      <section className="relative overflow-hidden bg-slate-900 py-24 md:py-32">
        <div className="pointer-events-none absolute left-1/2 top-0 h-[400px] w-[600px] -translate-x-1/2 rounded-full bg-[#8B7355]/10 blur-3xl" />
        <div className="relative mx-auto max-w-[660px] px-6 text-center">
          <h2
            className={`${serif} text-[clamp(2.2rem,5vw,3.4rem)] leading-[1.05] tracking-[-0.03em] text-white`}
          >
            Your next project deserves
            <br />a better schedule.
          </h2>
          <p
            className={`${sans} mx-auto mt-5 max-w-md text-[14.5px] font-light leading-relaxed text-slate-400`}
          >
            Set up your organization in under two minutes. No credit card, no
            sales call.
          </p>
          <div className="mt-9 flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/create-organization"
              className={`${sans} group inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-[14px] font-medium text-slate-900 transition hover:bg-slate-100`}
            >
              Start building
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </Link>
            <Link
              to="/login"
              className={`${sans} rounded-full border border-slate-700 px-7 py-3.5 text-[14px] font-medium text-slate-300 transition hover:border-slate-500 hover:text-white`}
            >
              Sign in
            </Link>
          </div>
        </div>
      </section>

      {/* ───────────────────── FOOTER ───────────────────── */}
      <footer className="relative overflow-hidden bg-[#0A0A0F]">
        {/* Top row: logo · links · copyright */}
        <div className="relative z-10 mx-auto grid max-w-[1200px] grid-cols-1 gap-10 px-6 pb-10 pt-16 md:grid-cols-[1fr_auto_auto] md:items-start md:gap-16 md:px-10 md:pt-20">
          {/* Left: Brand */}
          <Link
            to="/"
            className={`${serif} text-[1.4rem]  leading-none text-white/90`}
          >
            epicplan
          </Link>

          {/* Center: Terms links stacked */}
          <div className="flex flex-col gap-2.5">
            <span
              className={`${sans} text-[13.5px] text-white/50 transition hover:text-white/80 cursor-pointer`}
            >
              Terms &amp; Conditions
            </span>
            <span
              className={`${sans} text-[13.5px] text-white/50 transition hover:text-white/80 cursor-pointer`}
            >
              Privacy Policy
            </span>
          </div>

          {/* Right: Social icons + copyright */}
          <div className="flex flex-col items-end gap-4">
            <div className="flex items-center gap-3">
              <a
                href="https://twitter.com"
                target="_blank"
                rel="noopener noreferrer"
                className="flex h-10 w-10 items-center justify-center rounded-xl border border-white/10 text-white/40 transition hover:border-white/25 hover:text-white/70"
              >
                <Twitter className="h-[18px] w-[18px]" />
              </a>
              <a
                href="https://instagram.com"
                target="_blank"
                rel="noopener noreferrer"
                className="flex h-10 w-10 items-center justify-center rounded-xl border border-white/10 text-white/40 transition hover:border-white/25 hover:text-white/70"
              >
                <Instagram className="h-[18px] w-[18px]" />
              </a>
              <a
                href="https://linkedin.com"
                target="_blank"
                rel="noopener noreferrer"
                className="flex h-10 w-10 items-center justify-center rounded-xl border border-white/10 text-white/40 transition hover:border-white/25 hover:text-white/70"
              >
                <Linkedin className="h-[18px] w-[18px]" />
              </a>
            </div>
            <p
              className={`${sans} text-[12.5px] leading-relaxed text-white/30`}
            >
              © {new Date().getFullYear()} epicplan. All rights reserved.
            </p>
          </div>
        </div>

        {/* Giant fading brand text */}
        <div className="relative h-[200px] overflow-hidden md:h-[300px] lg:h-[380px] ">
          <div
            className={`${sans} pointer-events-none absolute left-1/2 top-0 -translate-x-1/2 whitespace-nowrap text-[clamp(10rem,28vw,24rem)] font-bold leading-[0.9] tracking-[-0.04em] text-white/[0.07] `}
            style={{
              maskImage:
                "linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,0.6) 40%, transparent 85%)",
              WebkitMaskImage:
                "linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,0.6) 40%, transparent 85%)",
            }}
          >
            epicplan
          </div>
        </div>
      </footer>
    </div>
  );
}

export default LandingPage;
